import Uppy, { Restrictions } from '@uppy/core';
import { useUppy } from '@uppy/react';
import { getPresignedUrlUploadParameters } from 'api/upload';
import { trackEvent } from 'services/analytics';
import { ContentUploadPreview, MediaType } from 'types/content';
import Bugsnag, { addErrorMetaData } from 'services/bugsnag';
import AwsS3 from '@uppy/aws-s3';

export interface TrendUppyProps {
  uppyId: string;
  uploadType: string;
  onUploadSuccess: (content: ContentUploadPreview) => void;
  setIsUploading?: (uploading: boolean) => void;
  onRemove: (id: string) => void;
  restrictions?: Restrictions;
}

const useTrendUppy = (
  componentName: string,
  {
    uppyId,
    uploadType,
    onUploadSuccess,
    setIsUploading,
    onRemove,
    restrictions,
  }: TrendUppyProps
) => {
  const uppy = useUppy(() => {
    return new Uppy({
      id: uppyId,
      restrictions,
      autoProceed: true,
      debug: process.env.NODE_ENV === 'development',
    })
      .use(AwsS3, {
        getUploadParameters: getPresignedUrlUploadParameters(uploadType),
      })
      .on('upload', () => {
        if (typeof setIsUploading !== 'undefined') {
          setIsUploading(true);
        }
      })
      .on('upload-success', (file, res) => {
        const fileId = file.id;
        const url = res.uploadURL!;
        const type: MediaType = file.type?.includes('video')
          ? 'video'
          : 'image';
        const removeItem = () => {
          uppy.removeFile(fileId);
        };
        onUploadSuccess({
          id: fileId,
          url,
          type,
          removeItem,
        });
        trackEvent('Creator Uploads Content', {
          name: file.name,
          type,
          fileId,
          uppyId,
          component: componentName,
        });
      })
      .on('complete', () => {
        if (typeof setIsUploading !== 'undefined') {
          setIsUploading(false);
        }
      })
      .on('error', (error) => {
        Bugsnag.notify(
          'Uppy Error',
          addErrorMetaData('error data', { error, uppyId })
        );
        if (typeof setIsUploading !== 'undefined') {
          setIsUploading(false);
        }
      })
      .on('file-removed', (file, _reason) => {
        onRemove(file.id);
      })
      .on('upload-error', (file, error, response) => {
        // TODO: Improve error handling cases
        Bugsnag.notify(
          'Uppy Content Upload Error',
          addErrorMetaData('error data', { file, error, response, uppyId })
        );
      });
  });

  return uppy;
};

export default useTrendUppy;
